import React, { useEffect, useState } from "react";
import { useBranch } from '../../../layouts/BranchContext';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";

import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
} from "@mui/icons-material";

import SearchIcon from "@mui/icons-material/Search";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

const Topic = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const { selectedBranchId } = useBranch();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const rowsPerPageOptions = [5, 10, 25, 50];

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [searchTerm, setSearchTerm] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [successModalType, setSuccessModalType] = useState("");

  const [selectedTopic, setSelectedTopic] = useState(null);

  const [topics, setTopics] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);

  const [courseMap, setCourseMap] = useState({});
  const [moduleMap, setModuleMap] = useState({});

  const [loading, setLoading] = useState(false);

  // --------------------------------------------------
  // SHOW SNACKBAR
  // --------------------------------------------------

   const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const getOrganizationId = () => {
    try {
      const raw = sessionStorage.getItem("user") || localStorage.getItem("user") || "{}";
      const user = JSON.parse(raw) || {};
      return user.organization_id ?? null;
    } catch {
      return null;
    }
  };

  const buildOrgBranchQuery = (organizationId, branchId) => {
    const params = new URLSearchParams();
    if (organizationId !== null && organizationId !== undefined && organizationId !== "") {
      params.append("organization", organizationId);
    }
    if (branchId !== null && branchId !== undefined && branchId !== "") {
      params.append("branch", branchId);
    }
    const query = params.toString();
    return query ? `?${query}` : "";
  };

  // --------------------------------------------------
  // FETCH ALL DATA
  // --------------------------------------------------

  const fetchAllData = async () => {
    setLoading(true);

    const token = localStorage.getItem("accessToken");

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    try {
      // -----------------------------
      // FETCH COURSES
      // -----------------------------

      const coursesRes = await axios.get(
        `${API_BASE_URL}/api/course/courses/`,
        {
          headers,
        }
      );

      const loadedCourses = Array.isArray(coursesRes.data)
        ? coursesRes.data
        : Array.isArray(coursesRes.data?.data)
        ? coursesRes.data.data
        : Array.isArray(coursesRes.data?.results)
        ? coursesRes.data.results
        : [];

      setCourses(loadedCourses);

      const cMap = {};

      loadedCourses.forEach((course) => {
        if (course?.id) {
          cMap[course.id] =
            course.course_name ||
            course.name ||
            course.courseName ||
            "";
        }
      });

      setCourseMap(cMap);

      // -----------------------------
      // FETCH MODULES
      // -----------------------------

           const orgBranchQuery = buildOrgBranchQuery(getOrganizationId(), selectedBranchId);

      const modulesRes = await axios.get(
        `${API_BASE_URL}/api/course/modules/${orgBranchQuery}`,
        {
          headers,
        }
      );

      const loadedModules = Array.isArray(modulesRes.data)
        ? modulesRes.data
        : Array.isArray(modulesRes.data?.data)
        ? modulesRes.data.data
        : Array.isArray(modulesRes.data?.results)
        ? modulesRes.data.results
        : [];

      setModules(loadedModules);

      const mMap = {};

      loadedModules.forEach((module) => {
        if (module?.id) {
          mMap[module.id] =
            module.module_name ||
            module.name ||
            module.moduleName ||
            "";
        }
      });

      setModuleMap(mMap);

      // -----------------------------
      // FETCH TOPICS
      // -----------------------------

            const topicsRes = await axios.get(
        `${API_BASE_URL}/api/course/topics/${orgBranchQuery}`,
        {
          headers,
        }
      );

      const loadedTopics = Array.isArray(topicsRes.data)
        ? topicsRes.data
        : Array.isArray(topicsRes.data?.data)
        ? topicsRes.data.data
        : Array.isArray(topicsRes.data?.results)
        ? topicsRes.data.results
        : [];

      setTopics(loadedTopics);
      setFilteredData(loadedTopics);
      setPage(1);
    } catch (error) {
      console.error("Failed to load topic data:", error);

      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Failed to load courses, modules, or topics.";

      showSnackbar(message, "error");
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // INITIAL LOAD
  // --------------------------------------------------

   useEffect(() => {
    fetchAllData();
  }, [selectedBranchId]);

  // --------------------------------------------------
  // REFRESH TOPICS
  // --------------------------------------------------

    const refreshTopics = async () => {
    const token = localStorage.getItem("accessToken");

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    try {
      const topicQuery = buildOrgBranchQuery(getOrganizationId(), selectedBranchId);

      const response = await axios.get(
        `${API_BASE_URL}/api/course/topics/${topicQuery}`,
        {
          headers,
        }
      );

      const loadedTopics = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data?.results)
        ? response.data.results
        : [];

      setTopics(loadedTopics);
      setFilteredData(loadedTopics);

      setPage(1);
    } catch (error) {
      console.error("Refresh topics failed:", error);

      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Failed to refresh topics.";

      showSnackbar(message, "error");
    }
  };

  // --------------------------------------------------
  // DELETE MODAL
  // --------------------------------------------------

  const handleShowDeleteModal = (topic) => {
    if (!topic?.id) {
      showSnackbar("Invalid topic selected.", "error");
      return;
    }

    setSelectedTopic(topic);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedTopic(null);
  };

  // --------------------------------------------------
  // DELETE TOPIC
  // --------------------------------------------------

  const handleDelete = async () => {
    if (!selectedTopic?.id) {
      showSnackbar("Cannot delete: topic ID is missing.", "error");
      handleCloseDeleteModal();
      return;
    }

    const topicId = selectedTopic.id;

    const topicName =
      selectedTopic.topic_name ||
      selectedTopic.TopicName ||
      "Topic";

    setLoading(true);

    const token = localStorage.getItem("accessToken");

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};

    try {
      await axios.delete(
        `${API_BASE_URL}/api/course/topics/${topicId}/`,
        {
          headers,
        }
      );

      // Remove deleted topic immediately from UI
      setTopics((previousTopics) =>
        previousTopics.filter(
          (topic) => topic.id !== topicId
        )
      );

      setFilteredData((previousTopics) =>
        previousTopics.filter(
          (topic) => topic.id !== topicId
        )
      );

      setShowDeleteModal(false);
      setSelectedTopic(null);

      showSnackbar(
        `"${topicName}" deleted successfully.`,
        "success"
      );

      // Sync latest backend data
      await refreshTopics();
    } catch (error) {
      console.error("Delete failed:", error);

      let message = "Failed to delete topic.";

      if (error?.response?.status === 404) {
        message =
          "Topic not found. It may already have been deleted.";
      } else if (error?.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.message) {
        message = error.message;
      }

      showSnackbar(message, "error");
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // SEARCH
  // --------------------------------------------------

  useEffect(() => {
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      setFilteredData(topics);
      setPage(1);
      return;
    }

    const filtered = topics.filter((topic) => {
      const courseName = (
        courseMap[topic?.course] || ""
      ).toLowerCase();

      const moduleName = (
        moduleMap[topic?.module] || ""
      ).toLowerCase();

      const topicName = (
        topic?.topic_name ||
        topic?.TopicName ||
        ""
      ).toLowerCase();

      return (
        courseName.includes(term) ||
        moduleName.includes(term) ||
        topicName.includes(term)
      );
    });

    setFilteredData(filtered);
    setPage(1);
  }, [
    searchTerm,
    topics,
    courseMap,
    moduleMap,
  ]);

  // --------------------------------------------------
  // PAGINATION
  // --------------------------------------------------

  const totalPages = Math.ceil(
    filteredData.length / rowsPerPage
  );

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleChangePage = (_, newPage) => {
    if (
      newPage < 1 ||
      (totalPages > 0 && newPage > totalPages)
    ) {
      return;
    }

    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(
      event.target.value,
      10
    );

    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  // --------------------------------------------------
  // HELPERS
  // --------------------------------------------------

  const getCourseName = (topic) => {
    return (
      courseMap[topic?.course] ||
      topic?.course_name ||
      topic?.courseName ||
      "—"
    );
  };

  const getModuleName = (topic) => {
    return (
      moduleMap[topic?.module] ||
      topic?.module_name ||
      topic?.moduleName ||
      "—"
    );
  };

  const getTopicName = (topic) => {
    return (
      topic?.topic_name ||
      topic?.TopicName ||
      "—"
    );
  };

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <Container
      maxWidth={false}
      sx={{
        width: "100%",
        px: {
          xs: 1,
          sm: 2,
          md: 3,
        },
      }}
    >
      {/* ==================================================
          HEADER
      ================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1,
          p: 2,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* LEFT */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          {/* SEARCH */}

          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(event) =>
              setSearchTerm(event.target.value)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color:
                        theme.palette.primary.main,
                      ml: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),

              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setSearchTerm("")
                    }
                    sx={{
                      color:
                        theme.palette.text.secondary,
                      mr: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),

              sx: {
                borderRadius: "30px",

                backgroundColor: isDark
                  ? "rgba(255,255,255,0.08)"
                  : theme.palette.background.paper,

                boxShadow: isDark
                  ? "0 2px 8px rgba(0,0,0,0.3)"
                  : "0 2px 8px rgba(0,0,0,0.1)",

                "& .MuiOutlinedInput-notchedOutline":
                  {
                    border: `1px solid ${
                      isDark
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(0,0,0,0.12)"
                    }`,
                  },

                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.12)"
                    : theme.palette.action.hover,

                  boxShadow: isDark
                    ? "0 2px 12px rgba(0,0,0,0.4)"
                    : "0 2px 12px rgba(0,0,0,0.15)",
                },

                "&.Mui-focused": {
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.14)"
                    : theme.palette.background.paper,

                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,

                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      border: `2px solid ${theme.palette.primary.main}`,
                    },
                },

                pl: 1.5,
                height: "50px",
                transition: "all 0.3s ease",
              },
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 350,
              },
              maxWidth: "100%",

              "& .MuiInputBase-input": {
                padding: "10px 14px",
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />

          {/* ADD TOPIC */}

          <Button
            variant="contained"
            onClick={() =>
              navigate("/add-topic")
            }
            startIcon={<AddIcon />}
            sx={{
              ml: {
                xs: 0,
                sm: 1,
              },

              width: {
                xs: "auto",
                sm: 160,
              },

              minWidth: {
                xs: 130,
                sm: 160,
              },

              borderRadius: "10px",
              height: "50px",
              px: 2,

              fontWeight: "bold",

              backgroundColor:
                theme.palette.primary.main,

              "&:hover": {
                backgroundColor:
                  theme.palette.primary.dark,
              },

              whiteSpace: "nowrap",
            }}
          >
            ADD TOPIC
          </Button>
        </Box>

        {/* USER GUIDE */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={() =>
              setShowUserGuideModal(true)
            }
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              color: theme.palette.primary.main,
            }}
          >
            <InfoOutlineIcon
              sx={{
                fontSize: 30,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* ==================================================
          TABLE
      ================================================== */}

      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
          mx: "auto",
          backgroundColor:
            theme.palette.background.paper,
          borderRadius: "16px",
        }}
      >
        <TableContainer
          sx={{
            width: "100%",
            maxHeight:
              "calc(100vh - 300px)",

            overflow: "auto",

            "&::-webkit-scrollbar": {
              width: 8,
              height: 8,
            },

            "&::-webkit-scrollbar-thumb": {
              backgroundColor:
                theme.palette.primary.main,
              borderRadius: 10,
            },

            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },

            scrollbarWidth: "thin",

            scrollbarColor: `${theme.palette.primary.main} transparent`,
          }}
        >
          <Table
            stickyHeader
            sx={{
              minWidth: 650,

              "& .MuiTableHead-root .MuiTableCell-root":
                {
                  fontWeight: "bold",
                  backgroundColor:
                    theme.palette.primary.main,
                  color: "#fff",
                  whiteSpace: "nowrap",
                },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    minWidth: 70,
                    whiteSpace: "nowrap",
                  }}
                >
                  S.No
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    minWidth: 200,
                    whiteSpace: "nowrap",
                  }}
                >
                  Course
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    minWidth: 200,
                    whiteSpace: "nowrap",
                  }}
                >
                  Module
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    minWidth: 200,
                    whiteSpace: "nowrap",
                  }}
                >
                  Topic
                </TableCell>

                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    minWidth: 120,
                    whiteSpace: "nowrap",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{
                      py: 5,
                      color:
                        theme.palette.text.secondary,
                    }}
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((topic, index) => (
                  <TableRow
                    key={topic.id}
                    hover
                    sx={{
                      backgroundColor:
                        theme.palette.background.paper,

                      "&:hover": {
                        backgroundColor:
                          theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        color: theme.palette.text.primary,
                      }}
                    >
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    {/* COURSE */}

                    <TableCell
                      sx={{
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        color:
                          theme.palette.text.primary,
                      }}
                    >
                      {getCourseName(topic)}
                    </TableCell>

                    {/* MODULE */}

                    <TableCell
                      sx={{
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        color:
                          theme.palette.text.primary,
                      }}
                    >
                      {getModuleName(topic)}
                    </TableCell>

                    {/* TOPIC */}

                    <TableCell
                      sx={{
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        color:
                          theme.palette.text.primary,
                      }}
                    >
                      {getTopicName(topic)}
                    </TableCell>

                    {/* ACTION */}

                    <TableCell align="center">
                      {/* EDIT */}

                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() =>
                            navigate(
                              `/edit-topic/${topic.id}`
                            )
                          }
                          sx={{
                            color:
                              theme.palette.primary.main,

                            "&:hover": {
                              backgroundColor:
                                theme.palette.action
                                  .hover,
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      {/* DELETE */}

                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() =>
                            handleShowDeleteModal(
                              topic
                            )
                          }
                          sx={{
                            "&:hover": {
                              backgroundColor:
                                theme.palette.action
                                  .hover,
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{
                      fontSize: "1rem",
                      py: 4,
                      color:
                        theme.palette.text.disabled,
                    }}
                  >
                    No Data Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ==================================================
          PAGINATION
      ================================================== */}

      {filteredData.length > 0 && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mt: 1,
            p: 2,
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color:
                theme.palette.primary.main,
            }}
          >
            Page <strong>{page}</strong> of{" "}
            <strong>{totalPages}</strong>
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flexWrap: "wrap",
            }}
          >
            {/* PREVIOUS */}

            <IconButton
              onClick={() =>
                handleChangePage(
                  null,
                  page - 1
                )
              }
              disabled={page === 1}
              sx={{
                color:
                  theme.palette.primary.main,

                "&:hover": {
                  backgroundColor:
                    theme.palette.action.hover,
                },

                "&.Mui-disabled": {
                  opacity: 0.3,
                },

                borderRadius: "50%",
                width: 36,
                height: 36,
              }}
            >
              <ChevronLeft />
            </IconButton>

            {/* PAGE NUMBERS */}

            {[
              ...Array(totalPages),
            ].map((_, index) => {
              const pageNumber =
                index + 1;

              return (
                <IconButton
                  key={pageNumber}
                  onClick={() =>
                    handleChangePage(
                      null,
                      pageNumber
                    )
                  }
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",

                    backgroundColor:
                      page === pageNumber
                        ? theme.palette.primary
                            .main
                        : "transparent",

                    color:
                      page === pageNumber
                        ? theme.palette.primary
                            .contrastText
                        : theme.palette.text
                            .primary,

                    "&:hover": {
                      backgroundColor:
                        page === pageNumber
                          ? theme.palette.primary
                              .dark
                          : theme.palette.action
                              .hover,
                    },
                  }}
                >
                  {pageNumber}
                </IconButton>
              );
            })}

            {/* NEXT */}

            <IconButton
              onClick={() =>
                handleChangePage(
                  null,
                  page + 1
                )
              }
              disabled={page >= totalPages}
              sx={{
                color:
                  theme.palette.primary.main,

                "&:hover": {
                  backgroundColor:
                    theme.palette.action.hover,
                },

                "&.Mui-disabled": {
                  opacity: 0.3,
                },

                borderRadius: "50%",
                width: 36,
                height: 36,
              }}
            >
              <ChevronRight />
            </IconButton>

            {/* ROWS */}

            <FormControl
              size="small"
              sx={{
                minWidth: 100,
                ml: 1,
              }}
            >
              <InputLabel
                id="rows-per-page-label"
                sx={{
                  color:
                    theme.palette.text.secondary,

                  "&.Mui-focused": {
                    color:
                      theme.palette.primary.main,
                  },
                }}
              >
                Rows
              </InputLabel>

              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={
                  handleChangeRowsPerPage
                }
                label="Rows"
                sx={{
                  height: 36,
                  color:
                    theme.palette.text.primary,

                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme.palette.divider,
                    },

                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme.palette.primary.main,
                    },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme.palette.primary.main,
                    },
                }}
              >
                {rowsPerPageOptions.map(
                  (option) => (
                    <MenuItem
                      key={option}
                      value={option}
                    >
                      {option}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* ==================================================
          SNACKBAR
      ================================================== */}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() =>
          setOpenSnackbar(false)
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(false)
          }
          severity={snackbarSeverity}
          sx={{
            width: "100%",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ==================================================
          DELETE MODAL
      ================================================== */}

      <Modal
        open={showDeleteModal}
        onClose={
          handleCloseDeleteModal
        }
        BackdropProps={{
          style: {
            backgroundColor:
              "rgba(0, 0, 0, 0.8)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",

            width: {
              xs: "90%",
              sm: 400,
            },

            bgcolor:
              "background.paper",

            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              p: "12px 16px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#fff",
              }}
            >
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* CONTENT */}

          <Box
            sx={{
              p: 2.5,
            }}
          >
            <Typography
              sx={{
                mb: 2,
                color:
                  theme.palette.text.primary,
              }}
            >
              Are you sure you want to delete{" "}
              <strong>
                {getTopicName(
                  selectedTopic || {}
                )}
              </strong>
              ?
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent:
                  "flex-end",
                mt: 3,
                gap: 1,
              }}
            >
              <Button
                onClick={
                  handleCloseDeleteModal
                }
                variant="outlined"
               data-app-cancel="true">
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading
                  ? "Deleting..."
                  : "Yes, Delete"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ==================================================
          SUCCESS MODAL
      ================================================== */}

      <Modal
        open={showSuccessModal}
        onClose={() =>
          setShowSuccessModal(false)
        }
        BackdropProps={{
          style: {
            backgroundColor:
              "rgba(0, 0, 0, 0.8)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",

            width: {
              xs: "90%",
              sm: 400,
            },

            bgcolor:
              "background.paper",

            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              p: "12px 16px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#fff",
              }}
            >
              {successModalType === "add"
                ? "Topic Added ✅"
                : "Topic Updated ✅"}
            </Typography>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <Typography
              sx={{
                mb: 2,
                color:
                  theme.palette.text.primary,
              }}
            >
              {successModalType === "add"
                ? "Topic Added Successfully"
                : "Topic Updated Successfully"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent:
                  "flex-end",
                mt: 3,
              }}
            >
              <Button
                onClick={() =>
                  setShowSuccessModal(
                    false
                  )
                }
                variant="contained"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ==================================================
          USER GUIDE MODAL
      ================================================== */}

      <Modal
        open={showUserGuideModal}
        onClose={() =>
          setShowUserGuideModal(false)
        }
        BackdropProps={{
          style: {
            backgroundColor:
              "rgba(0, 0, 0, 0.8)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",

            width: {
              xs: "92%",
              sm: 650,
              md: 800,
            },

            bgcolor:
              "background.paper",

            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,

              p: "12px 16px",

              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "#fff",
                fontWeight: "bold",
              }}
            >
              Topic Module - User Guide
            </Typography>

            <IconButton
              onClick={() =>
                setShowUserGuideModal(
                  false
                )
              }
              sx={{
                color: "#fff",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* CONTENT */}

          <Box
            sx={{
              maxHeight: "70vh",
              overflowY: "auto",

              p: 2.5,

              "&::-webkit-scrollbar": {
                width: 8,
              },

              "&::-webkit-scrollbar-thumb":
                {
                  backgroundColor:
                    theme.palette.primary
                      .main,
                  borderRadius: 10,
                },

              "&::-webkit-scrollbar-track":
                {
                  backgroundColor:
                    "transparent",
                },
            }}
          >
            {/* SECTION 1 */}

            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mb: 1,
              }}
            >
              ➤ Follow these steps to add a
              new topic:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Click the{" "}
                <strong>
                  ADD TOPIC
                </strong>{" "}
                button to open the add topic
                form.
              </li>

              <li>
                Enter the Topic Name and Topic
                Content.
              </li>

              <li>
                Optionally upload a PDF file.
              </li>

              <li>
                Review all entered information.
              </li>

              <li>
                Click{" "}
                <strong>SAVE</strong>{" "}
                to add the topic.
              </li>

              <li>
                The new topic will appear in the
                Topic List.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.secondary,
              }}
            >
              ⚠️ Note: Course, Module, Topic
              Name, and Topic Content are
              required fields. The PDF file is
              optional.
            </Typography>

            {/* SECTION 2 */}

            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to edit an
              existing topic:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Select the topic from the Topic
                List.
              </li>

              <li>
                Click the{" "}
                <strong>Edit</strong>{" "}
                pencil icon.
              </li>

              <li>
                Update the required information.
              </li>

              <li>
                Optionally upload a new PDF.
              </li>

              <li>
                Click{" "}
                <strong>UPDATE</strong>.
              </li>

              <li>
                Click{" "}
                <strong>CANCEL</strong>{" "}
                to discard changes.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.secondary,
              }}
            >
              ⚠️ Note: Course, Module, Topic
              Name, and Topic Content are
              required fields.
            </Typography>

            {/* SECTION 3 */}

            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to delete a
              topic:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Select the topic you want to
                delete.
              </li>

              <li>
                Click the{" "}
                <strong>Delete</strong>{" "}
                icon.
              </li>

              <li>
                Confirm by clicking{" "}
                <strong>
                  Yes, Delete
                </strong>.
              </li>

              <li>
                The topic will be removed from
                the list.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.secondary,
              }}
            >
              ⚠️ Note: Deleted topics cannot be
              recovered.
            </Typography>

            {/* SECTION 4 */}

            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to search
              for a topic:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Use the search bar at the top.
              </li>

              <li>
                Search by course name, module
                name, or topic name.
              </li>

              <li>
                Results update automatically.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.secondary,
              }}
            >
              ⚠️ Note: Search is
              case-insensitive.
            </Typography>

            {/* SECTION 5 */}

            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to navigate
              the topic list:
            </Typography>

            <ul
              style={{
                marginLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>
                Use the pagination controls.
              </li>

              <li>
                Click the left/right arrows.
              </li>

              <li>
                Select a specific page.
              </li>

              <li>
                Change rows per page using the
                dropdown.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  theme.palette.text.secondary,
              }}
            >
              ⚠️ Note: Rows per page options
              are 5, 10, 25, and 50.
            </Typography>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default Topic;